import React, { useEffect, useState } from "react";
import { Box, Typography, Divider, CircularProgress } from "@mui/material";
import { Gift, ClipboardCheck } from "lucide-react";
import { getGiftsApi } from "../../api/cardApi";

const GiftReservedComponent = ({ uniqueCode }) => {
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!uniqueCode) return;

    const fetchGifts = async () => {
      try {
        setLoading(true);
        const data = await getGiftsApi(uniqueCode);
        setGifts(data || []);
      } catch (err) {
        console.error("Error loading gift data:", err);
        setError("Gagal memuatkan data hadiah.");
      } finally {
        setLoading(false);
      }
    };

    fetchGifts();

    const intervalId = setInterval(fetchGifts, 15000);
    return () => clearInterval(intervalId);
  }, [uniqueCode]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} sx={{ color: "#68D2D2" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography align="center" sx={{ py: 4, color: "#c62828", fontSize: 13 }}>
        {error}
      </Typography>
    );
  }

  const reservedGifts = gifts.filter((g) => g.isReserved);

  return (
    <Box
      sx={{
        backgroundColor: "white",
        px: 2,
        py: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {/* Title */}
      <Typography sx={{ fontSize: 22 }}>Status</Typography>

      {/* Summary card */}
      <Box
        sx={{
          mt: 3,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 2,
          width: "100%",
          maxWidth: 500,
        }}
      >
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "18px",
            p: 2,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            textAlign: "center",
            justifyItems: "center",
            alignContent: "center",
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              borderRadius: "50%",
              border: "solid 2px #2494a3",
              color: "#2494a3",
            }}
          >
            <Gift size={18} />
          </span>
          <Typography sx={{ fontSize: 13, mt: 1 }}>Jumlah Item</Typography>
          <Typography sx={{ fontSize: 22, fontWeight: "bold" }}>
            {gifts.length}
          </Typography>
        </Box>

        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "18px",
            p: 2,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            textAlign: "center",
            justifyItems: "center",
            alignContent: "center",
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              borderRadius: "50%",
              border: "solid 2px #2e7d32",
              color: "#2e7d32",
            }}
          >
            <ClipboardCheck size={18} />
          </span>
          <Typography sx={{ fontSize: 13, mt: 1 }}>Ditempah</Typography>
          <Typography sx={{ fontSize: 22, fontWeight: "bold" }}>
            {reservedGifts.length}
          </Typography>
        </Box>
      </Box>

      {/* List */}
      <Box
        sx={{
          mt: 4,
          width: "100%",
          maxWidth: 600,
        }}
      >
        <Typography sx={{ fontSize: 15, mb: 2 }}>Senarai Pemberi</Typography>

        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "16px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            overflow: "hidden",
          }}
        >
          {/* Table Header */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "2fr 1.5fr 1fr",
              px: 3,
              py: 1.2,
              backgroundColor: "#F3F3F3",
              color: "#4F4F4F",
            }}
          >
            <Typography sx={{ fontSize: 13 }}>Nama</Typography>
            <Typography sx={{ fontSize: 13 }}>Hadiah</Typography>
            <Typography sx={{ fontSize: 13 }} align="center">
              Status
            </Typography>
          </Box>

          {/* Table Body */}
          <Box
            sx={{
              maxHeight: 350,
              overflowY: "scroll",
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none",
            }}
          >
            {gifts.map((gift, index) => (
              <Box key={gift.id || index}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1.5fr 1fr",
                    alignItems: "center",
                    px: 3,
                    py: 1.4,
                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                  }}
                >
                  {/* Guest name */}
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                      {gift.guestName || "-"}
                    </Typography>
                    {gift.phoneNumber && (
                      <Typography sx={{ fontSize: 11, color: "#888", mt: 0.3 }}>
                        {gift.phoneNumber}
                      </Typography>
                    )}
                  </Box>

                  {/* Gift name */}
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "#555",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      pr: 1,
                    }}
                  >
                    {gift.giftName || "-"}
                  </Typography>

                  {/* Reserved badge */}
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Box
                      sx={{
                        py: 0.5,
                        borderRadius: "999px",
                        fontSize: 11,
                        letterSpacing: "0.2px",
                        backgroundColor: gift.isReserved
                          ? "rgba(76,175,80,0.12)"
                          : "rgba(0,0,0,0.06)",
                        color: gift.isReserved ? "#2e7d32" : "#666",
                        minWidth: 72,
                        textAlign: "center",
                      }}
                    >
                      {gift.isReserved ? "Ditempah" : "Belum"}
                    </Box>
                  </Box>
                </Box>

                {index !== gifts.length - 1 && (
                  <Divider sx={{ opacity: 0.05 }} />
                )}
              </Box>
            ))}

            {gifts.length === 0 && (
              <Typography
                sx={{
                  textAlign: "center",
                  fontSize: 13,
                  py: 3,
                  opacity: 0.6,
                }}
              >
                Tiada tempahan hadiah
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default GiftReservedComponent;
